import styled from 'styled-components';
import { updatePredictiveAudienceGoalMutation } from 'api/mutations/predictiveAudiences';
import { useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import {
  EditPredictiveGoalResponse,
  EditPredictiveGoalVariables,
} from 'modules/predictiveGoals/types';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { useCoreGoalsContext } from 'modules/predictiveGoals/components/contexts/CoreGoalsContext/CoreGoalsContext';
import { InlineEdit } from 'ui/inlineEdit/InlineEdit';
import { FC } from 'react';
import { heading3, spacing } from 'ui/theme';
import { isValidGoalProperty } from 'modules/predictiveGoals/utils';
import { PROVIDE_GOAL_NAME, PROVIDE_VALID_GOAL_NAME } from 'modules/predictiveGoals/constants';

const InlineContainer = styled.div`
  margin-bottom: ${spacing.double};
  width: 50%;

  & button {
    padding-left: 0 !important;
    margin-left: 0 !important;
  }

  & span {
    ${heading3};
  }
`;

export interface EditGoalInlineProps {
  goalVariables: EditPredictiveGoalVariables;
  goalName: string;
  onUpdateGoalName: (goalName: string) => void;
}

export const EditGoalInline: FC<EditGoalInlineProps> = ({
  goalVariables,
  goalName,
  onUpdateGoalName,
}) => {
  const {
    goals: { retrigger },
  } = useCoreGoalsContext();
  const dispatch = useDispatch();
  const [updateGoal] = useMutation<EditPredictiveGoalResponse, EditPredictiveGoalVariables>(
    updatePredictiveAudienceGoalMutation,
    {
      onCompleted: () => {
        retrigger();
        dispatch(addPositiveToast('Goal name saved'));
      },
      onError: (error) => {
        dispatch(addNegativeToast(error.message || 'Unable to save goal name'));
      },
    },
  );

  const onUpdate = () => {
    // No change in goal name
    if (goalName === goalVariables.name) {
      return;
    }
    const errors = [];

    if (!goalName) {
      errors.push(PROVIDE_GOAL_NAME);
    }
    if (!isValidGoalProperty(goalName)) {
      errors.push(PROVIDE_VALID_GOAL_NAME);
    }
    if (errors.length > 0) {
      errors.map((validationError) => dispatch(addNegativeToast(validationError)));
      return;
    }

    updateGoal({
      variables: {
        ...goalVariables,
        name: goalName,
      },
    });
  };

  return (
    <InlineContainer>
      <InlineEdit
        isRequired
        onBlur={onUpdate}
        onChange={onUpdateGoalName}
        placeholder="Goal name"
        value={goalName}
        variant="pageHeader"
      />
    </InlineContainer>
  );
};
